<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <div v-if="props.label" class="combobox-label" :class="{ 'is-required': props.required }">
      {{ props.label }}
      <span v-if="props.required" class="required-indicator">*</span>
    </div>

    <div class="entity-combobox" ref="comboboxRef" v-bind="filteredAttrs">
      <div
        class="combobox-input-wrapper"
        :class="{
          'is-open': isOpen,
          'has-selection': !isOpen && hasSelection,
          'has-avatar':
            (!isOpen && hasSelection && shouldShowAvatar(selectedOption)) ||
            (freeTextEntry && freeTextMatchedItem && shouldShowAvatar(freeTextMatchedItem)),
          'has-error': Array.isArray(error) ? error.length > 0 : !!error,
          'is-disabled': disabled,
        }"
        @click="handleTriggerClick">
        <!-- Free text mode -->
        <template v-if="freeTextEntry">
          <div
            v-if="freeTextMatchedItem && shouldShowAvatar(freeTextMatchedItem)"
            class="entity-avatar"
            :class="getAvatarBgClass(freeTextMatchedItem)">
            <img
              v-if="freeTextMatchedItem.imageUrl"
              :src="freeTextMatchedItem.imageUrl"
              :alt="resolveLabel(freeTextMatchedItem)" />
            <span v-else class="initials">{{ getInitialsForItem(freeTextMatchedItem) }}</span>
          </div>
          <input
            ref="inputRef"
            v-model="freeTextValue"
            type="text"
            class="combobox-input"
            :placeholder="placeholder"
            @input="handleFreeTextInput"
            @keydown.down.prevent="navigate('down')"
            @keydown.up.prevent="navigate('up')"
            @keydown.enter.prevent.stop="handleFreeTextEnter"
            @keydown.esc.prevent="closeDropdown"
            @focus="openDropdown"
            @blur="handleInputBlur" />
        </template>

        <!-- Standard combobox mode -->
        <template v-else>
          <input
            v-if="isOpen"
            ref="inputRef"
            v-model="searchQuery"
            type="text"
            class="combobox-input"
            :placeholder="hasSelection && selectedOption ? resolveLabel(selectedOption) : placeholder"
            @keydown.down.prevent="navigate('down')"
            @keydown.up.prevent="navigate('up')"
            @keydown.enter.prevent.stop="selectHighlighted"
            @keydown.esc.prevent="closeDropdown"
            @blur="handleInputBlur" />

          <div v-else class="selection-display">
            <template v-if="hasSelection && selectedOption">
              <div
                v-if="shouldShowAvatar(selectedOption)"
                class="entity-avatar"
                :class="getAvatarBgClass(selectedOption)">
                <img
                  v-if="selectedOption.imageUrl"
                  :src="selectedOption.imageUrl"
                  :alt="resolveLabel(selectedOption)" />
                <span v-else class="initials">{{ getInitialsForItem(selectedOption) }}</span>
              </div>
              <span class="selected-name">{{ resolveLabel(selectedOption) }}</span>
            </template>
            <span v-else class="placeholder-text">{{ placeholder }}</span>
          </div>
        </template>

        <div v-if="loading" class="combobox-inline-spinner" aria-label="Loading">
          <div class="inline-spinner-circle"></div>
        </div>
        <button
          v-else-if="showClear"
          type="button"
          class="combobox-clear"
          @mousedown.prevent
          @click.stop="handleClear"
          tabindex="-1"
          aria-label="Clear selection">
          <IconX size="16" />
        </button>
        <button
          v-else
          type="button"
          class="combobox-chevron"
          :disabled="disabled"
          @mousedown.prevent
          @click.stop="handleChevronToggle"
          tabindex="-1"
          :aria-label="isOpen ? 'Close dropdown' : 'Open dropdown'">
          <IconChevronDown class="chevron-icon" :class="{ 'rotate-180': isOpen }" size="16" />
        </button>
      </div>

      <transition name="fade">
        <div v-if="isOpen" class="combobox-dropdown">
          <div v-if="loading && filteredItems.length === 0" class="loading-state">
            <div class="spinner"></div>
            <p>Loading...</p>
          </div>

          <ul v-else-if="filteredItems.length > 0" class="dropdown-list" role="listbox">
            <template v-for="(item, index) in filteredItems" :key="resolveValue(item) ?? resolveLabel(item)">
              <!-- Free-text entry: what the user typed is itself a valid choice,
                   so it gets a row of its own instead of an empty dropdown. -->
              <FreeTextCreateOption
                v-if="isFreeTextCreateOption(item)"
                :label="resolveLabel(item)"
                :highlighted="index === highlightedIndex"
                @mousedown.prevent="selectItem(item)"
                @mouseenter="highlightedIndex = index" />

              <li
                v-else
                class="dropdown-item"
                :data-testid="`lead-combobox-option-${encodeURIComponent(resolveLabel(item))}`"
                :class="{
                  'is-highlighted': index === highlightedIndex,
                  'is-selected': isSelected(item),
                  'has-avatar': shouldShowAvatar(item) && !$slots.option,
                }"
                @mousedown.prevent="selectItem(item)"
                @mouseenter="highlightedIndex = index"
                role="option">
                <slot v-if="$slots.option" name="option" v-bind="item"></slot>

                <template v-else>
                  <div v-if="shouldShowAvatar(item)" class="entity-avatar" :class="getAvatarBgClass(item)">
                    <img v-if="item.imageUrl" :src="item.imageUrl" :alt="resolveLabel(item)" />
                    <span v-else class="initials">{{ getInitialsForItem(item) }}</span>
                  </div>
                  <span class="item-name">{{ resolveLabel(item) }}</span>
                </template>
              </li>
            </template>
          </ul>

          <NoResults v-else :search-query="searchValue" :message="searchValue ? props.emptyMessage : undefined">
            <template v-if="$slots['no-results']" #no-results="{ searchQuery: sq }">
              <slot name="no-results" :searchQuery="sq" />
            </template>
          </NoResults>
        </div>
      </transition>
    </div>
    <FormErrorList
      v-if="error && (Array.isArray(error) ? error.length > 0 : true)"
      :errors="Array.isArray(error) ? error : [error]" />
    <div v-if="$slots.hint" class="hint-text">
      <slot name="hint"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useAttrs, computed, ref, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useDropdownManager } from '@/composables/useDropdownManager';
import { IconChevronDown, IconX } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import NoResults from './NoResults.vue';
import FreeTextCreateOption from './FreeTextCreateOption.vue';
import { getInitials } from '@/helpers/helpers';
import {
  freeTextCreateLabel,
  isFreeTextCreateOption,
  useFreeTextCreateOption,
} from '@/composables/useFreeTextCreateOption';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const filteredAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: className, style, ...rest } = attrs;
  return rest;
});

interface OptionItem {
  id?: string | number;
  name?: string;
  imageUrl?: string;
  initials?: string;
  colorVariant?: string;
  [key: string]: any;
}

const props = defineProps<{
  label?: string;
  modelValue: OptionItem | string | null;
  items: OptionItem[];
  placeholder?: string;
  displayInitials?: boolean;
  getOptionLabel?: string | ((option: OptionItem) => string);
  getOptionValue?: string | ((option: OptionItem) => any);
  reduce?: (option: OptionItem) => any;
  loading?: boolean;
  error?: string | string[] | null;
  required?: boolean;
  disableFiltering?: boolean; // to enable/disable client-side filtering
  disabled?: boolean;
  freeTextEntry?: boolean;
  clearable?: boolean;
  emptyMessage?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
  (e: 'search', query: string): void;
  (e: 'option:selected', option: OptionItem): void;
  (e: 'option:created', value: string): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const { isOpen, open: openDropdownManager, close: closeDropdownManager } = useDropdownManager();
const freeTextValue = ref('');
let lastEmittedSearch: string | null = null;

const emitSearch = (query: string) => {
  if (query === lastEmittedSearch) return;
  lastEmittedSearch = query;
  emit('search', query);
};

// Find a matching item for the current free text value (for avatar display)
const freeTextMatchedItem = computed(() => {
  if (!props.freeTextEntry || !freeTextValue.value) return null;
  return props.items.find((item) => resolveLabel(item).toLowerCase() === freeTextValue.value.toLowerCase()) || null;
});

const resolveLabel = (option: OptionItem): string => {
  if (!option) return '';
  const createLabel = freeTextCreateLabel(option);
  if (createLabel !== null) return createLabel;
  if (typeof props.getOptionLabel === 'function') {
    return props.getOptionLabel(option);
  }
  const key = (props.getOptionLabel as string) || 'name';
  return option[key] ?? '';
};

const resolveValue = (option: OptionItem): any => {
  if (!option) return null;
  if (typeof props.getOptionValue === 'function') {
    return props.getOptionValue(option);
  }
  const key = (props.getOptionValue as string) || 'id';
  return option[key];
};

// Sync freeTextValue with modelValue when in freeTextEntry mode
watch(
  () => props.modelValue,
  (newVal) => {
    if (props.freeTextEntry) {
      if (typeof newVal === 'string') {
        freeTextValue.value = newVal;
      } else if (newVal && typeof newVal === 'object') {
        freeTextValue.value = resolveLabel(newVal);
      } else {
        freeTextValue.value = '';
      }
    }
  },
  { immediate: true }
);

const handleFreeTextInput = () => {
  // Emit the raw text value on every keystroke
  emit('update:modelValue', freeTextValue.value);
  emitSearch(freeTextValue.value);
  if (!isOpen.value && freeTextValue.value) {
    openDropdown();
  }
};

const handleFreeTextEnter = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredItems.value.length) {
    // User highlighted a result — select it
    selectItem(filteredItems.value[highlightedIndex.value]);
  } else {
    closeDropdown();
  }
};

// --- Selection Logic ---
const selectedOption = computed(() => {
  if (props.freeTextEntry) return null;
  if (props.modelValue === null || props.modelValue === undefined) return null;
  if (typeof props.modelValue === 'string') {
    return props.items.find((item) => resolveValue(item) === props.modelValue) || null;
  }
  if (!props.reduce) return props.modelValue as OptionItem;

  return (
    props.items.find((item) => {
      const reducedValue = props.reduce!(item);
      return reducedValue === props.modelValue;
    }) || null
  );
});

const hasSelection = computed(() => !!selectedOption.value);

const isSelected = (item: OptionItem): boolean => {
  // the create row offers a value that doesn't exist yet — it is never a selection
  if (isFreeTextCreateOption(item)) return false;
  if (props.freeTextEntry) {
    return resolveLabel(item) === freeTextValue.value;
  }
  if (!selectedOption.value) return false;
  return resolveValue(selectedOption.value) === resolveValue(item);
};

const searchQuery = ref('');
// unified search term — either the combobox search input or the free-text value
const searchValue = computed(() => (props.freeTextEntry ? freeTextValue.value : searchQuery.value));
const highlightedIndex = ref(-1);
const comboboxRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

watch(searchQuery, (newQuery) => {
  if (!props.freeTextEntry) {
    emitSearch(newQuery);
  }
});

// shared with LeadTreeCombobox — see useFreeTextCreateOption for when the row is
// offered and why.
const freeTextCreateOption = useFreeTextCreateOption({
  enabled: () => props.freeTextEntry,
  loading: () => props.loading,
  text: () => freeTextValue.value,
  hasExactMatch: (label) => props.items.some((item) => resolveLabel(item).toLowerCase() === label.toLowerCase()),
});

const filteredItems = computed(() => {
  let items: OptionItem[];

  if (props.disableFiltering) {
    items = props.items;
  } else {
    const query = searchValue.value;
    items = query
      ? props.items.filter((item) => resolveLabel(item).toLowerCase().includes(query.toLowerCase()))
      : props.items;
  }

  // leads the list, so it owns index 0 of the navigable items
  return freeTextCreateOption.value ? [freeTextCreateOption.value, ...items] : items;
});

const openDropdown = async () => {
  if (isOpen.value) return;
  openDropdownManager();
  highlightedIndex.value = -1;

  emitSearch(searchValue.value);

  emit('open');
  await nextTick();
  inputRef.value?.focus();
};

const closeDropdown = () => {
  if (!isOpen.value) return;
  closeDropdownManager();
  if (!props.freeTextEntry) {
    searchQuery.value = '';
  }
  highlightedIndex.value = -1;
  emit('close');
};

const handleTriggerClick = () => {
  if (props.disabled) return;
  if (props.freeTextEntry) {
    inputRef.value?.focus();
    if (!isOpen.value) openDropdown();
  } else {
    if (isOpen.value) inputRef.value?.focus();
    else openDropdown();
  }
};

const selectItem = (item: OptionItem) => {
  // choosing the create row means "create what I typed", not "select an existing
  // option" — consumers distinguish the two through these events.
  if (isFreeTextCreateOption(item)) {
    emit('option:created', resolveLabel(item));
  } else {
    emit('option:selected', item);
  }

  if (props.freeTextEntry) {
    const label = resolveLabel(item);
    freeTextValue.value = label;
    emit('update:modelValue', label);
  } else {
    const valueToEmit = props.reduce ? props.reduce(item) : item;
    emit('update:modelValue', valueToEmit);
  }

  closeDropdown();
};

const navigate = (direction: 'up' | 'down') => {
  if (!isOpen.value) {
    openDropdown();
    return;
  }
  const total = filteredItems.value.length;
  if (total === 0) return;
  if (direction === 'down') {
    highlightedIndex.value = (highlightedIndex.value + 1) % total;
  } else {
    highlightedIndex.value = (highlightedIndex.value - 1 + total) % total;
  }
};

const selectHighlighted = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredItems.value.length) {
    selectItem(filteredItems.value[highlightedIndex.value]);
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (comboboxRef.value && !comboboxRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

const handleInputBlur = (event: FocusEvent) => {
  if (comboboxRef.value && !comboboxRef.value.contains(event.relatedTarget as Node)) {
    closeDropdown();
  }
};

const showClear = computed(() => {
  if (!props.clearable || props.disabled) return false;
  if (props.freeTextEntry) return !!freeTextValue.value;
  return hasSelection.value;
});

const handleClear = (event: MouseEvent) => {
  event.stopPropagation();
  if (props.freeTextEntry) {
    freeTextValue.value = '';
  }
  emit('update:modelValue', null);
  emitSearch(searchValue.value);
  openDropdown();
};

const handleChevronToggle = () => {
  if (props.disabled) return;
  if (isOpen.value) {
    closeDropdown();
    inputRef.value?.blur();
  } else {
    openDropdown();
  }
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));

const getInitialsForItem = (item: OptionItem): string => {
  return getInitials(item, resolveLabel, props.displayInitials ?? false);
};

const shouldShowAvatar = (item: OptionItem | null): boolean => {
  if (!item) return false;
  // show avatar if item has imageUrl or if we have initials to display
  return !!(item.imageUrl || getInitialsForItem(item));
};

const getAvatarBgClass = (item: OptionItem) => {
  if (!item) return 'bg-variant-gray';
  const variant = item.colorVariant || 'gray';
  return `bg-variant-${variant}`;
};
</script>

<style scoped>
.hint-text {
  margin-top: 6px !important;
  font-size: 12px;
  font-weight: 400;
  color: #475467;
}

.combobox-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
  user-select: none;
}

.entity-combobox {
  position: relative;
  width: 100%;
}

.combobox-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 4px 12px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  box-shadow: 0px 1px 2px 0px #1018280d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.combobox-input-wrapper.has-avatar {
  padding-left: 4px;
}

.combobox-input-wrapper:hover {
  border-color: var(--tblr-primary);
}

.combobox-input-wrapper.is-disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

.combobox-input-wrapper.is-disabled:hover {
  border-color: #d0d5dd;
}

.combobox-input-wrapper.is-open {
  border-color: var(--tblr-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.combobox-input-wrapper.has-error {
  border-color: #ef4444;

  &:hover,
  &.is-open {
    border-color: #ef4444;
  }

  &.is-open {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
}

.combobox-input {
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  font-size: 16px;
  background: transparent;
  color: #111827;
  height: 100%;
  line-height: 20px;
}

.selection-display {
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;
}

.placeholder-text {
  color: #9ca3af;
  font-size: 14px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-name {
  font-size: 16px;
  font-weight: 400;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chevron-icon {
  width: 20px;
  height: 20px;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.chevron-icon.rotate-180 {
  transform: rotate(180deg);
}

.combobox-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.2s ease;
}

.combobox-clear:hover {
  color: #111827;
}

.combobox-inline-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.inline-spinner-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--tblr-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.combobox-chevron {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.combobox-chevron:disabled {
  cursor: not-allowed;
}

.combobox-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  padding: 0 0 7px 0;
  background-color: #ffffff;
  border: 1px solid var(--tblr-primary);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0px 8px 8px 0px #00000026;
  z-index: 50;
  box-sizing: border-box;
}

.dropdown-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 42px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dropdown-item.has-avatar {
  padding-left: 4px;
}

.dropdown-item.is-highlighted {
  background-color: #f3f4f6;
}

.dropdown-item.is-selected {
  background-color: #eff6ff;
}

.item-name {
  flex-grow: 1;
  font-size: 16px;
  color: #111827;
}

.check-icon {
  width: 16px;
  height: 16px;
  color: var(--tblr-primary);
}

.entity-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  overflow: hidden;
  font-weight: 400;
  font-size: 13px;
  color: #000;
}

.entity-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.entity-avatar .initials {
  color: #000;
  font-weight: 400;
  font-size: 13px;
}

.bg-variant-gray {
  background-color: #e5e7eb;
}

.loading-state {
  padding: 24px 12px;
  text-align: center;
  color: #9ca3af;
}

.spinner {
  margin: 40px auto 0;
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--tblr-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin-top: 8px;
  margin-bottom: 40px;
  font-size: 11px;
  line-height: 24px;
  font-weight: 400;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.combobox-dropdown::-webkit-scrollbar {
  width: 6px;
}

.combobox-dropdown::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.combobox-dropdown::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
</style>
